import type { MissionManager, ShiftSnapshot } from './MissionManager';

export interface MissionHUDHandlers {
  onEndShiftClick: () => void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Partial<HTMLElementTagNameMap[K]> & { className?: string; text?: string } = {},
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (props.className) node.className = props.className;
  if (props.text != null) node.textContent = props.text;
  for (const [k, v] of Object.entries(props)) {
    if (k === 'className' || k === 'text') continue;
    (node as unknown as Record<string, unknown>)[k] = v;
  }
  return node;
}

export class MissionHUD {
  private bar: HTMLElement;
  private earningsEl: HTMLElement;
  private ridesEl: HTMLElement;
  private stateEl: HTMLElement;
  private timerEl: HTMLElement;
  private endShiftBtn: HTMLButtonElement;
  private toastHost: HTMLElement;
  private unsub: Array<() => void> = [];
  private lastTickRender = 0;

  constructor(manager: MissionManager, handlers: MissionHUDHandlers) {
    this.injectStyles();

    this.bar = el('div');
    this.bar.id = 'mission-hud';

    const left = el('div', { className: 'mh-left' });
    this.earningsEl = el('span', { className: 'mh-earnings', text: '$0' });
    this.ridesEl = el('span', { className: 'mh-rides', text: '0 rides' });
    left.append(this.earningsEl, this.ridesEl);

    const center = el('div', { className: 'mh-center' });
    this.stateEl = el('span', { className: 'mh-state', text: 'Find a taxi to start your shift' });
    this.timerEl = el('span', { className: 'mh-timer' });
    center.append(this.stateEl, this.timerEl);

    const right = el('div', { className: 'mh-right' });
    this.endShiftBtn = el('button', {
      className: 'am-btn am-btn-danger mh-end-shift',
      text: 'End Shift',
    });
    this.endShiftBtn.style.display = 'none';
    this.endShiftBtn.addEventListener('click', () => handlers.onEndShiftClick());
    right.append(this.endShiftBtn);

    this.bar.append(left, center, right);
    document.body.appendChild(this.bar);

    this.toastHost = el('div');
    this.toastHost.id = 'mission-toasts';
    document.body.appendChild(this.toastHost);

    this.unsub.push(
      manager.on('stateChanged', (s) => this.render(s)),
      manager.on('tick', (s) => this.renderTick(s)),
      manager.on('toast', (t) => this.showToast(t.message, t.tone)),
    );
  }

  private showResultCard(snapshot: ShiftSnapshot) {
    if (!snapshot.lastFare) return;
    const f = snapshot.lastFare;
    const card = el('div', { className: 'mh-result-card' });

    const row = (label: string, value: string, totalRow = false) => {
      const r = el('div', { className: totalRow ? 'mh-row mh-total' : 'mh-row' });
      r.append(el('span', { text: label }), el('span', { text: value }));
      return r;
    };

    card.append(
      row('Base fare', `$${f.baseFare.toFixed(2)}`),
      row('Time bonus', `+$${f.timeBonus.toFixed(2)}`),
      row('Clean ride', `+$${f.cleanBonus.toFixed(2)}`),
      row('Total', `$${f.total.toFixed(2)}`, true),
    );
    this.toastHost.appendChild(card);
    setTimeout(() => card.remove(), 2400);
  }

  private render(s: ShiftSnapshot) {
    this.earningsEl.textContent = `$${s.earnings.toFixed(0)}`;
    this.ridesEl.textContent = `${s.rides} ride${s.rides === 1 ? '' : 's'}`;
    this.endShiftBtn.style.display = s.state === 'idle' ? 'none' : 'inline-block';

    switch (s.state) {
      case 'idle':
        this.stateEl.textContent = 'Find a taxi to start your shift';
        this.timerEl.textContent = '';
        break;
      case 'awaiting_pickup':
        this.stateEl.textContent = 'Pick up the fare';
        this.timerEl.textContent = '';
        break;
      case 'en_route':
        this.stateEl.textContent = 'Deliver to destination';
        break;
      case 'result_card':
        this.stateEl.textContent = 'Ride complete!';
        this.timerEl.textContent = '';
        this.showResultCard(s);
        break;
    }
  }

  private renderTick(s: ShiftSnapshot) {
    const now = performance.now();
    if (now - this.lastTickRender < 100) return;
    this.lastTickRender = now;
    if (s.state === 'en_route' && s.timeRemaining != null) {
      const mm = Math.floor(s.timeRemaining / 60);
      const ss = Math.floor(s.timeRemaining % 60).toString().padStart(2, '0');
      this.timerEl.textContent = `${mm}:${ss}`;
    }
  }

  private showToast(msg: string, tone: 'info' | 'success' | 'warn') {
    const t = el('div', { className: `mh-toast mh-toast-${tone}`, text: msg });
    this.toastHost.appendChild(t);
    requestAnimationFrame(() => t.classList.add('mh-toast-show'));
    setTimeout(() => {
      t.classList.remove('mh-toast-show');
      setTimeout(() => t.remove(), 200);
    }, 1800);
  }

  private injectStyles() {
    if (document.getElementById('mh-styles')) return;
    const style = document.createElement('style');
    style.id = 'mh-styles';
    style.textContent = `
      /* Layout anchor — each zone positions itself so the state pill can
         never push into the mode indicator. Colors and button styles come
         from the global design tokens in index.html. */
      #mission-hud {
        position: fixed; top: 0; left: 0; right: 0;
        pointer-events: none; z-index: 100;
      }
      #mission-hud .mh-left,
      #mission-hud .mh-center {
        background: var(--am-glass);
        backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
        padding: 8px 14px; border-radius: var(--am-radius-pill);
        color: #fff;
        font-family: var(--am-font);
        font-size: 0.82rem; font-weight: 500;
        letter-spacing: 0.05em;
      }
      #mission-hud .mh-left {
        position: absolute; top: 16px; left: 16px;
        display: flex; gap: 12px;
      }
      #mission-hud .mh-earnings { font-weight: 700; color: var(--am-gold); }
      #mission-hud .mh-rides { opacity: 0.8; }
      #mission-hud .mh-center {
        position: absolute; top: 16px; left: 50%;
        transform: translateX(-50%);
        max-width: min(340px, 48vw);
        text-align: center;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      #mission-hud .mh-state { display: inline-block; margin-right: 8px; }
      #mission-hud .mh-timer {
        font-variant-numeric: tabular-nums;
        font-weight: 700; color: var(--am-gold);
      }
      #mission-hud .mh-right {
        position: fixed; bottom: 24px; right: 24px;
        pointer-events: auto; padding: 0; background: none;
      }
      /* On touch devices the bottom-right is owned by the ENTER/EXIT and
         JUMP buttons, so End Shift moves to the top-right and shrinks to
         a compact pill. We also tighten the HUD pills on narrow widths. */
      @media (pointer: coarse) {
        #mission-hud .mh-right {
          top: max(16px, env(safe-area-inset-top));
          right: max(16px, env(safe-area-inset-right));
          bottom: auto;
        }
        #mission-hud .mh-end-shift {
          font-size: 0.62rem;
          padding: 7px 14px;
        }
        #mission-hud .mh-left {
          top: max(16px, env(safe-area-inset-top));
          left: max(16px, env(safe-area-inset-left));
          font-size: 0.74rem;
          padding: 6px 12px; gap: 8px;
        }
        #mission-hud .mh-center {
          top: max(14px, env(safe-area-inset-top));
          font-size: 0.72rem;
          padding: 6px 12px;
          max-width: min(260px, 44vw);
        }
        #mission-toasts {
          top: max(76px, calc(env(safe-area-inset-top) + 60px));
        }
      }
      @media (max-width: 520px) {
        #mission-hud .mh-left { font-size: 0.66rem; gap: 6px; padding: 5px 10px; }
        #mission-hud .mh-center { font-size: 0.66rem; padding: 5px 10px; max-width: 40vw; }
      }

      #mission-toasts {
        position: fixed; top: 84px; left: 50%; transform: translateX(-50%);
        z-index: 100;
        display: flex; flex-direction: column; gap: 8px; align-items: center;
        pointer-events: none;
      }
      .mh-toast {
        font-family: var(--am-font);
        padding: 10px 18px; border-radius: var(--am-radius-pill);
        background: var(--am-glass-strong);
        backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
        color: #fff; font-size: 0.88rem; font-weight: 500;
        letter-spacing: 0.02em;
        opacity: 0; transform: translateY(-8px);
        transition: opacity 0.2s ease, transform 0.2s ease;
      }
      .mh-toast-show { opacity: 1; transform: translateY(0); }
      .mh-toast-success { background: var(--am-success); }
      .mh-toast-warn { background: var(--am-warn); }

      .mh-result-card {
        font-family: var(--am-font);
        background: var(--am-glass-strong);
        backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
        color: #fff;
        padding: 18px 24px; border-radius: var(--am-radius-card);
        min-width: 260px; font-size: 0.95rem;
        box-shadow: 0 10px 32px rgba(0, 0, 0, 0.45);
        animation: mh-slide-in 0.25s ease;
      }
      .mh-result-card .mh-row {
        display: flex; justify-content: space-between;
        padding: 5px 0; font-weight: 500;
      }
      .mh-result-card .mh-total {
        margin-top: 10px; padding-top: 10px;
        border-top: 1px solid rgba(255, 255, 255, 0.2);
        font-weight: 800; font-size: 1.1rem;
        color: var(--am-gold);
      }
      @keyframes mh-slide-in {
        from { opacity: 0; transform: translateY(-12px); }
        to   { opacity: 1; transform: translateY(0); }
      }
    `;
    document.head.appendChild(style);
  }

  destroy() {
    for (const u of this.unsub) u();
    this.bar.remove();
    this.toastHost.remove();
  }
}
